import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { Link } from 'gatsby'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Container from '@components/common/layout/Container'
import Section from '@components/common/layout/Section'
import IndividualsOrganizationsSection from '@components/home/IndividualsOrganizationsSection'
import NewsSection from '@components/home/NewsSection'
import NewsletterSection from '@components/home/NewsletterSection'
import OurOfficesSection from '@components/home/OurOfficesSection'
import SEO from '@components/layout/SEO'
import NavBar from '@components/navigation/NavBar'
import ArrowSvg from '@components/revamp/ArrowSvg'
import ExploreArticle from '@components/revamp/ExploreArticle'
import GridLayout from '@components/revamp/GridLayout'
import ClientsSection from '@components/shared/ClientsSection'

import landingImageDesktop from '@src/assets/images/femme-donnant-formation.jpg'
import i18n from '@src/config/i18n'
import locales from '@src/config/locales'

export default function HomePage({ location }) {
  const { t } = useTranslation(`revamp`)
  const locale = i18n.language
  const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false)
  // Define stats data
  const stats = [
    { value: t('stats.values.experience'), label: t('stats.experience') },
    { value: t('stats.values.satisfaction'), label: t('stats.satisfaction') },
    { value: t('stats.values.professionals'), label: t('stats.professionals') },
    // { value: t('stats.values.bilingual'), label: t('stats.bilingual') },
  ]

  // State for mobile stat carousel
  const [currentStatIndex, setCurrentStatIndex] = useState(0)
  const [isTransitionDisabled, setIsTransitionDisabled] = useState(false)

  // Data for the infinite carousel (clone first item)
  const carouselStats = [...stats, stats[0]]

  // Constants for carousel timing
  const TRANSITION_DURATION = 500
  const TRANSITION_DELAY = 50
  const CAROUSEL_INTERVAL = 7000

  // Effect for automatic rotation
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentStatIndex((prevIndex) => {
        const nextIndex = prevIndex + 1

        if (nextIndex === stats.length) {
          // If moving to the cloned first item
          // Wait for the transition to finish, then jump back
          setTimeout(() => {
            setIsTransitionDisabled(true) // Disable transition
            setCurrentStatIndex(0) // Jump to the actual first item

            // Re-enable transition shortly after the jump
            setTimeout(() => {
              setIsTransitionDisabled(false)
            }, TRANSITION_DELAY)
          }, TRANSITION_DURATION)
        }

        return nextIndex
      })
    }, CAROUSEL_INTERVAL)

    return () => clearInterval(intervalId) // Cleanup on unmount
  }, [stats.length])

  const handleOpenSearchMenu = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setIsSearchMenuOpen(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [setIsSearchMenuOpen]
  )

  return (
    <>
      <SEO
        title={t('revamp:mainTitle')}
        pathname={location.pathname}
        paths={{
          en: locales.en.homePath,
          fr: locales.fr.homePath,
        }}
      />
      <div className='h-50vh md:min-h-screen flex flex-col'>
        <header>
          <NavBar
            isSearchMenuOpen={isSearchMenuOpen}
            onSearchMenuClose={() => setIsSearchMenuOpen(false)}
          />
        </header>
        <div className='flex-1 flex flex-col'>
          <div className='relative flex-1 flex items-center'>
            {/* Desktop background */}
            <div
              className='md:block hidden absolute inset-0 bg-no-repeat'
              style={{
                backgroundImage: `radial-gradient(circle, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%), url('${landingImageDesktop}')`,
                backgroundPosition: 'top',
                backgroundSize: 'cover',
                zIndex: -1,
              }}
            />
            {/* Mobile background */}
            <div
              className='md:hidden block absolute inset-0 bg-no-repeat'
              style={{
                backgroundImage: `url('${landingImageDesktop}')`,
                backgroundPosition: 'top',
                backgroundSize: 'cover',
                height: '50vh',
                zIndex: -1,
              }}
            />
            <div className='hidden md:flex items-center w-full absolute bottom-24'>
              <Container className='w-full'>
                <h1 className='text-7xl font-bold leading-none text-white'>
                  {t('homeMainTitleFirstPart')}
                  <br />
                  {t('homeMainTitleSecondPart')}
                </h1>
                <p
                  className='mt-4 text-white text-2xl'
                  style={{ maxWidth: 600, fontWeight: 500, lineHeight: '1.875rem' }}
                >
                  {t('homeMainDescription')}
                </p>
                <div className='mt-10 flex gap-4'>
                  <Link
                    to={locales[locale].catalogGuaranteedSessionsPath}
                    className='hover:bg-revamp-gray-100 bg-white font-bold text-xl text-revamp-neutral-700 pl-3 pr-6 py-3 rounded-2xl flex items-center'
                  >
                    <ArrowSvg className={'mr-3 w-6'} color={'black'} />
                    {t('homeMainButtonGuaranteedSessions')}
                  </Link>
                  <button
                    type='button'
                    className='hover:bg-revamp-gray-100 bg-white font-bold text-xl text-revamp-neutral-700 pl-3 pr-6 py-3 rounded-2xl flex items-center'
                    onClick={handleOpenSearchMenu}
                  >
                    <MagnifyingGlassIcon className='text-black h-5 w-5' />
                    <span className='ml-3'>{t('homeMainButtonSearch')}</span>
                  </button>
                </div>
              </Container>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Banner Desktop */}
      <div className='hidden md:block bg-revamp-neutral-700 text-white py-6 -mt-1'>
        <Container className='mx-auto grid grid-cols-3 divide-x divide-white'>
          {/* Map desktop stats */}
          {stats.map((stat, index) => (
            <Stat key={index} value={stat.value} label={stat.label} />
          ))}
        </Container>
      </div>

      {/* Statistics Banner Mobile - Infinite Carousel */}
      <div className='block md:hidden bg-revamp-neutral-700 text-white py-2 -mt-1 w-full overflow-hidden'>
        <div
          className={`flex ${
            !isTransitionDisabled ? 'transition-transform duration-500 ease-in-out' : ''
          }`}
          style={{ transform: `translateX(-${currentStatIndex * 100}%)` }}
        >
          {carouselStats.map((stat, index) => (
            <div key={index} className='w-full flex-shrink-0'>
              <Container className='px-4 w-full'>
                <Stat value={stat.value} label={stat.label} />
              </Container>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile always learn */}
      <div className='block md:hidden bg-revamp-gray-50 py-6'>
        <Container>
          <h1 className='text-r-6xl leading-none'>
            <span className='font-bold'>{t('mobileAlwaysLearn.title')}</span>
            <br />
            {t('mobileAlwaysLearn.subtitle')}
          </h1>
          <p className='mt-4 text-lg'>{t('mobileAlwaysLearn.description')}</p>
          <div className='mt-6 flex flex-col gap-4 items-start'>
            <Link
              to={locales[locale].catalogGuaranteedSessionsPath}
              className='bg-revamp-neutral-700 text-white font-bold text-xl pl-3 pr-6 py-3 rounded-lg flex items-center'
            >
              <ArrowSvg className={'mr-3 w-6'} color={'white'} />
              {t('mobileAlwaysLearn.guaranteedSessions')}
            </Link>
            <button
              type='button'
              onClick={(e) => {
                handleOpenSearchMenu(e)
              }}
              className='bg-revamp-neutral-700 text-white font-bold text-xl pl-3 pr-6 py-3 rounded-lg flex items-center'
            >
              <div className='w-5 h-5 text-white'>
                <svg
                  height='18'
                  viewBox='0 0 17 18'
                  width='17'
                  xmlns='http://www.w3.org/2000/svg'
                  aria-hidden='true'
                  focusable='false'
                >
                  <path
                    d='m11.9946871 12.1875803 4.5964663 4.5964663c.1952621.1952622.1952621.5118446 0 .7071068-.1952622.1952621-.5118446.1952621-.7071068 0l-4.6544435-4.6544435c-1.1405155.8336101-2.54643314 1.3256901-4.0672031 1.3256901-3.81054237 0-6.9-3.0894576-6.9-6.9 0-3.81054237 3.08945763-6.9 6.9-6.9 3.8105424 0 6.9 3.08945763 6.9 6.9 0 1.92889462-.7916362 3.67302-2.0677129 4.9251803zm1.0677129-4.9251803c0-3.25825763-2.6417424-5.9-5.9-5.9-3.25825763 0-5.9 2.64174237-5.9 5.9 0 3.2582576 2.64174237 5.9 5.9 5.9 3.2582576 0 5.9-2.6417424 5.9-5.9z'
                    fill='currentColor'
                  />
                </svg>
              </div>
              <span className='ml-2'>{t('mobileAlwaysLearn.search')}</span>
            </button>
          </div>
        </Container>
      </div>

      <Section>
        <GridLayout className='items-center my-12 md3:my-24'>
          <h2 className='text-r-5xl font-bold leading-none' style={{ maxWidth: '25ch' }}>
            {t('homeColoredTitle.firstPart')}
            <br />
            <span className='text-revamp-green-500 text-r-7xl block' style={{ maxWidth: '15ch' }}>
              {t('homeColoredTitle.secondPart')}
            </span>
          </h2>
          <article className='leading-mid text-r-lg text-revamp-neutral-600 flex flex-col gap-6'>
            <p>{t('homeParagraphs.first')}</p>
            <p>{t('homeParagraphs.second')}</p>
            <p>{t('homeParagraphs.third')}</p>
          </article>
        </GridLayout>
        <IndividualsOrganizationsSection />
      </Section>
      <OurOfficesSection />
      <NewsSection />
      <div className='bg-light-blue overflow-hidden'>
        <Section className='pt-16 md3:pt-36 pb-8 md3:pb-20'>
          <ExploreArticle />
        </Section>
      </div>
      <ClientsSection />
      <NewsletterSection />
    </>
  )
}

interface StatProps {
  value: string | number
  label: string
  className?: string
}

const Stat: React.FC<StatProps> = ({ value, label, className = '' }) => {
  return (
    <div className={`px-4 py-4 md:py-0 flex flex-col items-center text-center ${className}`}>
      <p className='text-xl font-bold'>{value}</p>
      <p className='text-lg font-normal text-revamp-neutral-100'>{label}</p>
    </div>
  )
}
